import axios from 'axios';

/**
 * In development, use same-origin `/api` so the webpack dev server (craco) can proxy to Laravel.
 * That avoids requests accidentally going to `localhost:3000/api` without a backend and returning 404.
 * In production, set REACT_APP_BACKEND_URL to your API origin (e.g. https://api.example.com).
 */
const isDev = process.env.NODE_ENV === 'development';
const rawBackend = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
const API = isDev
  ? '/api'
  : rawBackend
    ? `${rawBackend}/api`
    : '/api';

const api = axios.create({
  baseURL: API,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('protego_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const accountId = localStorage.getItem('protego_account_id');
  const profileId = localStorage.getItem('protego_profile_id');
  if (accountId) config.headers['X-Account-Id'] = accountId;
  if (profileId) config.headers['X-Profile-Id'] = profileId;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      if (!url.includes('/auth/login') && !url.includes('/auth/signup')) {
        localStorage.removeItem('protego_token');
        localStorage.removeItem('protego_user');
        localStorage.removeItem('protego_account_id');
        localStorage.removeItem('protego_profile_id');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  signup: (data) => api.post('/auth/signup', data),
  me: () => api.get('/auth/user'),
  logout: () => api.post('/auth/logout'),
};

export const sellersAPI = {
  getAll: (params) => api.get('/sellers', { params }),
  getOverview: (id, params) => api.get(`/sellers/${id}/overview`, { params }),
  getMetrics: (id, { category, dateFrom, dateTo } = {}) => api.get(`/sellers/${id}/metrics`, { params: { category, date_from: dateFrom || undefined, date_to: dateTo || undefined } }),
  getAlerts: (id, severity) => api.get(`/sellers/${id}/alerts`, { params: { severity } }),
  getSignals: (id, severity, category) => api.get(`/sellers/${id}/signals`, { params: { severity, category } }),
  getHistory: (id) => api.get(`/sellers/${id}/history`),
  getPreemptiveActions: (id) => api.get(`/sellers/${id}/preemptive-actions`),
  sync: (id, data) => api.post(`/sellers/${id}/sync`, data),
  syncByProfile: (data) => api.post('/sellers/sync-profile', data),
  getSignalOrders: (sellerId, signalId) => api.get(`/sellers/${sellerId}/signals/${signalId}/orders`),
  getAsinRisks: (id) => api.get(`/sellers/${id}/asin-risks`),
  getTasks: (id, status) => api.get(`/sellers/${id}/tasks`, { params: { status } }),
  createTask: (id, data) => api.post(`/sellers/${id}/tasks`, data),
  updateTask: (sellerId, taskId, data) => api.patch(`/sellers/${sellerId}/tasks/${taskId}`, data),
  getAuditReport: (id) => api.get(`/sellers/${id}/audit-report`),
};

export const adminAPI = {
  getSellers: () => api.get('/admin/sellers'),
  getStats: () => api.get('/admin/stats'),
  bulkSync: () => api.post('/admin/bulk-sync'),
};

export const poaAPI = {
  getTemplates: () => api.get('/poa-templates'),
  getTemplate: (metricName) => api.get(`/poa-templates/${encodeURIComponent(metricName)}`),
};

export const settingsAPI = {
  getProfile: () => api.get('/settings/profile'),
  updateProfile: (data) => api.put('/settings/profile', data),
};

export const accountsAPI = {
  getAll: () => api.get('/accounts'),
  getMarketplaces: () => api.get('/marketplaces'),
  startSpConnect: (data) => api.post('/sp/connect', data),
  getProfiles: (accountId) => api.get(`/accounts/${accountId}/profiles`),
  updateSupportAccess: (id, payload) => api.post(`/accounts/${id}/support-access`, payload),
};

export const amazonSupportAPI = {
  getPolicyViolations: (accountId, { dateFrom, dateTo } = {}) => api.get(`/accounts/${accountId}/policy-violations`, { params: { date_from: dateFrom || undefined, date_to: dateTo || undefined } }),
  getVoiceOfCustomer: (accountId, { dateFrom, dateTo } = {}) => api.get(`/accounts/${accountId}/voice-of-customer`, { params: { date_from: dateFrom || undefined, date_to: dateTo || undefined } }),
  getPerformanceNotifications: (accountId, { dateFrom, dateTo } = {}) => api.get(`/accounts/${accountId}/performance-notifications`, { params: { date_from: dateFrom || undefined, date_to: dateTo || undefined } }),
  triggerScrape: (accountId) => api.post(`/accounts/${accountId}/trigger-scrape`),
  getScrapeLogs: (accountId, lines = 200) => api.get(`/accounts/${accountId}/scrape-logs`, { params: { lines, _ts: Date.now() } }),
};

export const seedAPI = {
  seed: () => api.post('/seed'),
};

export default api;
